import type { GattCharacteristic, GattService } from './types';
import { uuid128 } from './uuids';

export type Unsubscribe = () => void;

/** Subscribe to notifications/indications. Returns an unsubscribe function. */
export async function subscribe(
  char: GattCharacteristic,
  onValue: (dv: DataView) => void,
): Promise<Unsubscribe> {
  const handler = (ev: Event) => {
    const target = ev.target as GattCharacteristic | null;
    const v = target?.value ?? char.value;
    if (v) onValue(v);
  };
  char.addEventListener('characteristicvaluechanged', handler);
  await char.startNotifications();
  return () => {
    char.removeEventListener('characteristicvaluechanged', handler);
    char.stopNotifications().catch(() => undefined);
  };
}

export async function tryGetService(
  getter: { getPrimaryService(uuid: BluetoothServiceUUID): Promise<GattService> },
  uuid: string | number,
): Promise<GattService | null> {
  try {
    return await getter.getPrimaryService(uuid128(uuid));
  } catch {
    return null;
  }
}

export async function tryGetChar(
  service: GattService,
  uuid: string | number,
): Promise<GattCharacteristic | null> {
  try {
    return await service.getCharacteristic(uuid128(uuid));
  } catch {
    return null;
  }
}

export async function writeChar(
  char: GattCharacteristic,
  bytes: Uint8Array,
  withResponse = true,
): Promise<void> {
  const copy = new Uint8Array(bytes); // own ArrayBuffer (not shared/offset)
  if (withResponse && char.properties.write) {
    await char.writeValueWithResponse(copy);
  } else if (char.properties.writeWithoutResponse) {
    await char.writeValueWithoutResponse(copy);
  } else {
    await char.writeValueWithResponse(copy);
  }
}
